import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {EditComponent} from 'web-shared';
import {IntegratedMasterService} from '../../integrated-master-editor/services/integrated-master.service';
import {NgForm} from '@angular/forms';
import {
  ApiResponse,
  ClassificatorValuesQuery,
  DateHelpers,
  GLOBAL,
  LeModalService,
  UsersQuery
} from 'common-shared';
import {ItemGroupInItemGroupModel, ManufacturingItemGroupModel} from 'web-shared';
import {ManufacturingItemGroupService} from '../manufacturing-item-group.service';
import {HierarchyNodeEditModalComponent} from '../../../common/hierarchy-nodes/hierarchy-node-edit-modal/hierarchy-node-edit-modal.component';
import {StateService} from 'web-shared';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UnitService} from 'web-shared';
import {HierarchyNodeModel} from "../../../common/hierarchy-nodes/models/hierarchy-node.model";
import {HierarchyNodeService} from "../../../common/hierarchy-nodes/hierarchy-node.service";
import {NgbNavChangeEvent} from "@ng-bootstrap/ng-bootstrap";
import {ManufacturingItemGroupEditModalComponent} from "../manufacturing-item-group-edit-modal/manufacturing-item-group-edit-modal.component";
import {BaseConnectionModel} from "web-shared";
import {SortEvent} from "primeng/api";
import {IAppSettings} from "web-shared";
import {SpecLinesEditComponent} from "../../../common/spec/spec-lines-edit/spec-lines-edit.component";
import {ResourceService} from "../../resources/resource.service";

@Component({
  selector: 'ime-manufactured-item-group-edit',
  templateUrl: './manufacturing-item-group-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManufacturingItemGroupEditComponent extends EditComponent implements OnInit, OnChanges {
  @Input() response: ApiResponse<any>;
  @Input() manufacturedItemGroup: ManufacturingItemGroupModel;
  @Input() selectedTab: string;
  @Input() shouldResetSaveState$: Subject<boolean>;
  @Input() wasValidated$: Subject<boolean> = new Subject<boolean>();
  @Input() readonly: boolean;
  @Input() scrollIntoViewId: string;
  @ViewChild(NgForm, {static: true}) form: NgForm;
  @Output() changed = new EventEmitter<boolean>();
  @Output() setTab = new EventEmitter<string>();
  settings: IAppSettings;
  showPropertyLocalizations: boolean = false;
  @ViewChild(SpecLinesEditComponent, {static: false}) specLinesEditComponent: SpecLinesEditComponent;
  modifiedBy: string;
  hierarchyNodes: HierarchyNodeModel[];
  Classificators = GLOBAL.Classificators;
  toConnectionFirstRow: number = 0;
  fromConnectionFirstRow: number = 0;
  toConnectionRowsPerPage: number = 10;
  fromConnectionRowsPerPage: number = 10;

  constructor(public classificatorValuesQuery: ClassificatorValuesQuery, public manufacturedItemGroupService: ManufacturingItemGroupService, private integratedMasterService: IntegratedMasterService, private modalService: LeModalService, private stateService: StateService, private usersQuery: UsersQuery, public unitService: UnitService, private hierarchyNodeService: HierarchyNodeService, private resourceService: ResourceService, private changeDetector: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.selectedTab ??= 'general';
    this.form.statusChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.changed.emit(this.form.pristine);
    });

    this.stateService.settings$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.settings = value
      if (this.settings.Ime.ShowHierarchy) {
        this.getHierarchyNodes();
      }
    });

    this.shouldResetSaveState$?.pipe(takeUntil(this.componentDestroyed)).subscribe(() => this.setFormPristine());
  }

  setFormPristine(): void {
    Object.keys(this.form.controls).forEach(control => {
      this.form.controls[control].markAsPristine();
    });
    this.changed.emit(this.form.pristine);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.selectedTab || changes.manufacturedItemGroup?.currentValue) && this.selectedTab === 'ime' && this.manufacturedItemGroup) {
      this.ensureImeLoaded();
    }
    if (changes.manufacturedItemGroup?.currentValue) {
      this.generateModifiedMessage();
    }
  }

  selectTab($event: NgbNavChangeEvent) {
    if ($event?.nextId) {
      this.setTab.emit($event.nextId);
    }
  }

  ensureImeLoaded(force = false) {
    if (!this.manufacturedItemGroup.isImeLoaded() && !this.manufacturedItemGroup.SaveRouting || force) {
      this.integratedMasterService.getAll(this.manufacturedItemGroup.Id).pipe(takeUntil(this.componentDestroyed)).subscribe({
        next: value => {
          this.loadIme(value);
        }, error: value => {
          this.loadIme(value);
        }
      });
    }
  }

  private loadIme(value) {
    this.integratedMasterService.loadIme(this.manufacturedItemGroup, value);
    this.changeDetector.detectChanges();
  }

  initialize(manufacturedItemGroup: ManufacturingItemGroupModel) {
    this.manufacturedItemGroup = manufacturedItemGroup;
    if (this.manufacturedItemGroup.SaveRouting) {
      this.ensureImeLoaded(true);
    }
  }

  showHierarchy(): void {
    const initialSelection = Array.from(this.manufacturedItemGroup.HierarchyNodes);
    const modalRef = this.modalService.show(HierarchyNodeEditModalComponent, {initialState: {selectedIds: this.manufacturedItemGroup.HierarchyNodes}, ignoreBackdropClick: true});

    modalRef.content.onHide$.pipe(takeUntil(this.componentDestroyed)).subscribe(finalSelection => {
      const isChanged = initialSelection.length !== finalSelection.data.length || initialSelection.some(node => finalSelection.data.indexOf(node) === -1);
      if (isChanged) {
        this.manufacturedItemGroup.HierarchyNodes = finalSelection.data;
        this.getHierarchyNodes();
        this.changed.emit(!isChanged);
      }
    });
  }

  private getHierarchyNodes() {
    this.hierarchyNodeService.getRootWithHierarchy().pipe(takeUntil(this.componentDestroyed)).subscribe(root => {
      if (root) {
        this.hierarchyNodes = this.hierarchyNodeService.flatten(root.Children);
        this.changeDetector.detectChanges();
      }
    });
  }

  setChanged() {
    this.form.controls.code.markAsDirty();
    this.changed.emit(this.form.pristine);
  }

  private generateModifiedMessage() {
    if (this.manufacturedItemGroup.ModifiedBy === GLOBAL.Users.Admin) {
      this.modifiedBy = '';
    } else if (this.manufacturedItemGroup.ModifiedBy) {
      this.usersQuery.get$(this.manufacturedItemGroup.ModifiedBy).pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
        if (value) {
          this.modifiedBy = `Modified by ${value.Fullname ?? value.Email} on ${DateHelpers.formatDate(this.manufacturedItemGroup.ModifiedDate, 'dd.MM.yyyy HH:mm:ss')}`;
          this.changeDetector.detectChanges();
        }
      });
    }
  }

  removeConnection(connection: ItemGroupInItemGroupModel, type: 'To' | 'From') {
    if (type === 'To') {
      this.manufacturedItemGroup.Parents = this.manufacturedItemGroup.Parents.filter(value => value !== connection);
    } else {
      this.manufacturedItemGroup.Children = this.manufacturedItemGroup.Children.filter(value => value !== connection);
    }
    this.setChanged();
  }

  addConnection(type: 'To' | 'From') {
    let newConnection = new ItemGroupInItemGroupModel({$isNew: true});
    if (type === 'To') {
      newConnection.ChildId = this.manufacturedItemGroup.Id;
      this.manufacturedItemGroup.Parents = [...this.manufacturedItemGroup.Parents, newConnection];
      this.toConnectionFirstRow = BaseConnectionModel.getFirstRowNrOfLastPage(this.manufacturedItemGroup.Parents.length, this.toConnectionRowsPerPage);
    } else {
      newConnection.ParentId = this.manufacturedItemGroup.Id;
      this.manufacturedItemGroup.Children = [...this.manufacturedItemGroup.Children, newConnection];
      this.fromConnectionFirstRow = BaseConnectionModel.getFirstRowNrOfLastPage(this.manufacturedItemGroup.Children.length, this.fromConnectionRowsPerPage);
    }
    this.setChanged();
  }

  openItemGroup(itemGroupId: string = null) {
    this.modalService.show(ManufacturingItemGroupEditModalComponent, {initialState: {entityId: itemGroupId ?? this.manufacturedItemGroup.ParentId ?? this.manufacturedItemGroup.DraftId} as ManufacturingItemGroupEditModalComponent, class: 'modal-xl'});
  }

  setDateRange(dateRange: {From: Date, To: Date}, entity: ItemGroupInItemGroupModel) {
    if (entity.FromDate !== dateRange?.From || entity.ToDate !== dateRange?.To) {
      this.setChanged();
    }
    entity.FromDate = dateRange ? dateRange.From : null;
    entity.ToDate = dateRange ? dateRange.To : null;
  }

  trackByFunction = (index, item) => {
    return item.Id;
  }

  customSort(event: SortEvent) {
    if (event.field === 'ChildId' || event.field === 'ParentId') {
      this.manufacturedItemGroupService.getAll$.pipe(takeUntil(this.componentDestroyed)).subscribe(itemGroups => {
        let helperColumn = 'ItemGroup';
        event.data.map(data => {
          data[helperColumn] = itemGroups.find(itemGroup => itemGroup.Id === data[event.field])?.DisplayName;
        });
        event.field = helperColumn;
        BaseConnectionModel.sort(event);
      });
    } else {
      BaseConnectionModel.sort(event);
    }
  }
}
